import React from 'react';
import Icon from '../../../components/AppIcon';

const WorkerDetailsModal = ({ data, title, loading, onClose, onExport }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      approved: { bg: 'bg-success-100', text: 'text-success-800', label: 'Approved' },
      rejected: { bg: 'bg-error-100', text: 'text-error-800', label: 'Rejected' },
      pending: { bg: 'bg-warning-100', text: 'text-warning-800', label: 'Pending' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-1003 p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-xl font-bold text-text-primary">{title}</h2>
            <p className="text-text-secondary mt-1">{data.length} workers found</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onExport}
              className="flex items-center space-x-2 px-4 py-2 bg-success text-white rounded-md hover:bg-success-700 transition-colors duration-150"
            >
              <Icon name="Download" size={16} />
              <span>Export Excel</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[75vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3">
                <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                <span className="text-lg text-text-secondary">Loading worker details...</span>
              </div>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="mx-auto text-secondary-400 mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">No Workers Found</h3>
              <p className="text-text-secondary">No workers match the selected criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Worker ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Full Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Nationality</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Passport</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Gender</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Submission Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Recruiter</th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-secondary-200">
                  {data.map((worker, index) => (
                    <tr key={worker.id} className="hover:bg-secondary-50 transition-colors duration-150">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-medium text-primary">
                          {worker.worker_id || `W-${worker.id}`}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-800 rounded-full text-xs font-medium">
                            {worker.full_name ? worker.full_name.charAt(0).toUpperCase() : 'W'}
                          </div>
                          <div>
                            <div className="font-medium text-text-primary">{worker.full_name || 'N/A'}</div>
                            <div className="text-sm text-secondary-600">
                              {worker.date_of_birth ? `Born: ${new Date(worker.date_of_birth).toLocaleDateString()}` : 'DOB not available'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800">
                          {worker.nationality || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-text-primary">
                          {worker.passport_number || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <Icon 
                            name={worker.gender === 'Male' ? 'User' : worker.gender === 'Female' ? 'UserCheck' : 'Users'} 
                            size={16} 
                            className="text-secondary-600" 
                          />
                          <span className="text-sm text-text-primary">{worker.gender || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(worker.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-text-primary">
                          {new Date(worker.created_at).toLocaleDateString('en-MY', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div className="text-xs text-secondary-600">
                          {new Date(worker.created_at).toLocaleTimeString('en-MY', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-text-primary">
                          {worker.recruiters?.agency_name || 'Unknown'}
                        </div>
                        <div className="text-xs text-secondary-600">
                          {worker.recruiters?.country || 'N/A'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-secondary-200 bg-secondary-50">
          <div className="text-sm text-secondary-600">
            Showing {data.length} worker{data.length !== 1 ? 's' : ''}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-700 transition-colors duration-150"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkerDetailsModal; 